import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  createRedactedTelemetryEnvelope,
  normalizeTelemetryError,
  type TelemetryEnvelope,
  type TelemetryErrorKind,
  type TelemetryUrlParser,
} from '@automatick/shared-ts';

export const AppTelemetryBusinessArea = {
  Access: 'access',
  Account: 'account',
  Automation: 'automation',
  Communications: 'communications',
  Connections: 'connections',
  Home: 'home',
  Marketplace: 'marketplace',
  Operations: 'operations',
  Publishing: 'publishing',
  Security: 'security',
  Unknown: 'unknown',
  Work: 'work',
  Workspace: 'workspace',
} as const;

export type AppTelemetryBusinessArea =
  (typeof AppTelemetryBusinessArea)[keyof typeof AppTelemetryBusinessArea];

export const AppTelemetryRouteTemplate = {
  Account: 'account',
  Agents: 'agents',
  Approvals: 'approvals',
  Channel: 'channel',
  Connectors: 'connectors',
  Conversation: 'conversation',
  Credentials: 'credentials',
  Home: 'home',
  Marketplace: 'marketplace',
  Notifications: 'notifications',
  Operations: 'operations',
  PublicPublication: 'public-publication',
  Publishables: 'publishables',
  SignIn: 'sign-in',
  SignOut: 'sign-out',
  Skills: 'skills',
  Unknown: 'unknown',
  WorkRequests: 'work-requests',
  Workspaces: 'workspaces',
} as const;

export type AppTelemetryRouteTemplate =
  (typeof AppTelemetryRouteTemplate)[keyof typeof AppTelemetryRouteTemplate];

export interface AppTelemetryRouteContext {
  readonly area: AppTelemetryBusinessArea;
  readonly template: AppTelemetryRouteTemplate;
}

export interface AppTelemetryEventMap {
  readonly 'route.view': AppTelemetryRouteContext;
  readonly 'runtime.global-error': {
    readonly source: 'global-error-handler';
    readonly errorKind: TelemetryErrorKind;
  };
  readonly 'workspace.bootstrap.requested': {
    readonly trigger: 'application-start';
  };
  readonly 'workspace.switch.requested': {
    readonly trigger: 'workspace-selector';
  };
}

export type AppTelemetryEventName = keyof AppTelemetryEventMap;
export type AppTelemetryEnvelope = TelemetryEnvelope<AppTelemetryEventName>;
export type AppTelemetryEventArguments = {
  readonly [TEventName in AppTelemetryEventName]: readonly [
    eventName: TEventName,
    context: Readonly<AppTelemetryEventMap[TEventName]>,
  ];
}[AppTelemetryEventName];

export interface AppTelemetryTransport {
  emit(envelope: AppTelemetryEnvelope): void;
}

export interface AppTelemetryConfig {
  readonly enabled: boolean;
}

export const APP_TELEMETRY_CONFIG = new InjectionToken<AppTelemetryConfig>(
  'APP_TELEMETRY_CONFIG',
  {
    factory: () => Object.freeze({ enabled: false }),
  },
);

export const APP_TELEMETRY_TRANSPORT =
  new InjectionToken<AppTelemetryTransport | null>('APP_TELEMETRY_TRANSPORT', {
    factory: () => null,
  });

const appTelemetryUrlParser: TelemetryUrlParser = Object.freeze({
  parseAbsolute(value: string) {
    try {
      const parsed = new URL(value);
      return Object.freeze({
        protocol: parsed.protocol,
        hostname: parsed.hostname,
        username: parsed.username,
        password: parsed.password,
      });
    } catch {
      return null;
    }
  },
});

@Injectable()
export class AppConsoleTelemetryTransport implements AppTelemetryTransport {
  emit(envelope: AppTelemetryEnvelope): void {
    console.info('[automatick-telemetry]', envelope);
  }
}

@Injectable({ providedIn: 'root' })
export class AppTelemetryService {
  private readonly config = inject(APP_TELEMETRY_CONFIG);
  private readonly transport = inject(APP_TELEMETRY_TRANSPORT);

  track(...[eventName, context]: AppTelemetryEventArguments): void {
    if (!this.config.enabled || !this.transport) {
      return;
    }

    const envelope = createRedactedTelemetryEnvelope(
      eventName,
      new Date().toISOString(),
      context,
      appTelemetryUrlParser,
    );
    if (!envelope) {
      return;
    }

    try {
      this.transport.emit(envelope);
    } catch {
      // Telemetry must never alter application behavior.
    }
  }

  trackError(error: unknown): void {
    const summary = normalizeTelemetryError(error);
    this.track('runtime.global-error', {
      source: 'global-error-handler',
      errorKind: summary.kind,
    });
  }
}

export function classifyAppTelemetryRoute(
  rawRoute: string,
): AppTelemetryRouteContext {
  const segment = resolveRouteSegment(rawRoute);
  switch (segment) {
    case '':
    case 'home':
      return routeContext(
        AppTelemetryBusinessArea.Home,
        AppTelemetryRouteTemplate.Home,
      );
    case 'login':
      return routeContext(
        AppTelemetryBusinessArea.Access,
        AppTelemetryRouteTemplate.SignIn,
      );
    case 'logout':
      return routeContext(
        AppTelemetryBusinessArea.Access,
        AppTelemetryRouteTemplate.SignOut,
      );
    case 'publishables':
      return routeContext(
        AppTelemetryBusinessArea.Publishing,
        AppTelemetryRouteTemplate.Publishables,
      );
    case 'p':
    case 's':
      return routeContext(
        AppTelemetryBusinessArea.Publishing,
        AppTelemetryRouteTemplate.PublicPublication,
      );
    case 'work-requests':
      return routeContext(
        AppTelemetryBusinessArea.Work,
        AppTelemetryRouteTemplate.WorkRequests,
      );
    case 'approvals':
      return routeContext(
        AppTelemetryBusinessArea.Work,
        AppTelemetryRouteTemplate.Approvals,
      );
    case 'operations':
      return routeContext(
        AppTelemetryBusinessArea.Operations,
        AppTelemetryRouteTemplate.Operations,
      );
    case 'workspaces':
      return routeContext(
        AppTelemetryBusinessArea.Workspace,
        AppTelemetryRouteTemplate.Workspaces,
      );
    case 'credentials':
      return routeContext(
        AppTelemetryBusinessArea.Security,
        AppTelemetryRouteTemplate.Credentials,
      );
    case 'connectors':
      return routeContext(
        AppTelemetryBusinessArea.Connections,
        AppTelemetryRouteTemplate.Connectors,
      );
    case 'channel':
      return routeContext(
        AppTelemetryBusinessArea.Communications,
        AppTelemetryRouteTemplate.Channel,
      );
    case 'chat':
      return routeContext(
        AppTelemetryBusinessArea.Communications,
        AppTelemetryRouteTemplate.Conversation,
      );
    case 'skills':
      return routeContext(
        AppTelemetryBusinessArea.Automation,
        AppTelemetryRouteTemplate.Skills,
      );
    case 'marketplace':
      return routeContext(
        AppTelemetryBusinessArea.Marketplace,
        AppTelemetryRouteTemplate.Marketplace,
      );
    case 'agents':
      return routeContext(
        AppTelemetryBusinessArea.Automation,
        AppTelemetryRouteTemplate.Agents,
      );
    case 'account':
      return routeContext(
        AppTelemetryBusinessArea.Account,
        AppTelemetryRouteTemplate.Account,
      );
    case 'notifications':
      return routeContext(
        AppTelemetryBusinessArea.Communications,
        AppTelemetryRouteTemplate.Notifications,
      );
    default:
      return routeContext(
        AppTelemetryBusinessArea.Unknown,
        AppTelemetryRouteTemplate.Unknown,
      );
  }
}

function resolveRouteSegment(rawRoute: string): string | null {
  try {
    const parsed = new URL(rawRoute, 'https://telemetry.invalid');
    return parsed.pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
  } catch {
    return null;
  }
}

function routeContext(
  area: AppTelemetryBusinessArea,
  template: AppTelemetryRouteTemplate,
): AppTelemetryRouteContext {
  return Object.freeze({ area, template });
}
